import { useCallback, useEffect, useMemo, useState } from 'react'
import { Grid, Typography, CircularProgress } from '@material-ui/core'
import AddRoundedIcon from '@material-ui/icons/AddRounded'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { RouteContainer } from 'components/layout/RouteContainer/RouteContainer'
import * as LayoutStyled from 'components/layout/Layout.styles'
import { useCompanyId } from 'providers/CompanyProvider'
import { useGetLabelsQuery } from 'api/queries/labels/labels'
import { useAddLabelMutation, useDeleteLabel, useEditLabelMutation } from 'api/mutations/labels/labels'
import { COMPANY_LABELS_QUERY_KEY, OWN_LABELS_QUERY_KEY } from 'api/queriesKeys'
import { LabelsList } from 'components/data/LabelsList/LabelsList'
import { ADMIN_ROLE } from 'constants/roleGroups'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'
import { Label } from 'types/Label'
import { useHasRole } from 'hooks/useHasRoles'
import { LabelModal } from 'components/modals/LabelModal/LabelModal'
import * as Styled from './LabelsScreen.styles'

const LabelsScreen: React.FC = () => {
  const [isEditLabelModalOpen, setEditLabelModalOpen] = useState<boolean>(false)
  const { t } = useTranslation()
  const companyId = useCompanyId() || 0
  const queryClient = useQueryClient()
  const [loaderElement, setLoaderElement] = useState<HTMLDivElement | null>(null)
  const isAdmin = useHasRole(ADMIN_ROLE)
  const [editedLabel, setEditedLabel] = useState<Label>()

  const loader = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setLoaderElement(node)
    }
  }, [])

  const isIntersecting = useIntersectionObserver(loaderElement)

  const {
    data: labelsData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useGetLabelsQuery(isAdmin ? { companyId } : undefined)

  useEffect(() => {
    if (isIntersecting && hasNextPage) fetchNextPage()
  },
  [fetchNextPage, hasNextPage, isIntersecting])

  const { mutate: addLabel, isLoading: isAddLabelLoading } = useAddLabelMutation({
    onSuccess: () => {
      queryClient.invalidateQueries(OWN_LABELS_QUERY_KEY)
      queryClient.invalidateQueries(COMPANY_LABELS_QUERY_KEY)
      setEditLabelModalOpen(false)
    }
  }, isAdmin ? companyId : undefined)

  const { mutate: editLabel, isLoading: isEditLabelLoading } = useEditLabelMutation({
    onSuccess: () => {
      queryClient.invalidateQueries(OWN_LABELS_QUERY_KEY)
      queryClient.invalidateQueries(COMPANY_LABELS_QUERY_KEY)
    }
  })

  const { mutate: deleteLabel, isLoading: isDeleteLabelLoading } = useDeleteLabel({
    onSuccess: () => {
      queryClient.invalidateQueries(OWN_LABELS_QUERY_KEY)
      queryClient.invalidateQueries(COMPANY_LABELS_QUERY_KEY)
    }
  })

  const handleEdit = (label: Label) => {
    setEditedLabel(label)
    setEditLabelModalOpen(true)
  }

  const handleNew = () => {
    setEditedLabel(undefined)
    setEditLabelModalOpen(true)
  }

  const handleSave = (label: Omit<Label, 'id'>, id?: number) => {
    if (typeof id !== 'undefined') {
      editLabel({ id, ...label })
    } else {
      addLabel(label)
    }
  }

  const labels = useMemo(() => labelsData?.pages.reduce(
    (result, page) => (page?.content ? [...result, ...page.content] : result),
    [] as Label[]
  ) || [], [labelsData?.pages])

  return (
    <RouteContainer>
      <Grid container item xs={12} justifyContent='space-between' alignItems='center'>
        <Grid item>
          <Typography variant='h2'>
            {t('labels.title')}
          </Typography>
        </Grid>
        <Grid item>
          <Styled.Button
            backgroundGradient
            onClick={() => handleNew()}
            endIcon={<AddRoundedIcon />}
          >
            {t('labels.addNew')}
          </Styled.Button>
        </Grid>
        <LayoutStyled.HelperContainer item xs={12}>
          {isError && <Typography color='error'>{t('common.errors.generic')}</Typography>}
          {isLoading && <CircularProgress color='primary' />}
        </LayoutStyled.HelperContainer>
        <LayoutStyled.ListContainer item xs={12}>
          <LayoutStyled.HeadersRow>
            <LayoutStyled.HeaderText variant='subtitle1' $flexValue={0} $minWidth={52}>
              {t('common.id')}
            </LayoutStyled.HeaderText>
            <LayoutStyled.HeaderText variant='subtitle1'>
              {t('common.name')}
            </LayoutStyled.HeaderText>
            <LayoutStyled.HeaderText variant='subtitle1'>
              {t('common.type')}
            </LayoutStyled.HeaderText>
            <LayoutStyled.HeaderText
              variant='subtitle1'
              $flexValue={0}
              $minWidth={120}
              $textAlign='center'
            >
              {t('common.actions')}
            </LayoutStyled.HeaderText>
          </LayoutStyled.HeadersRow>
          <LabelsList labels={labels} onDelete={(id) => deleteLabel(id)} onEdit={handleEdit} />
          <Styled.LoadMoreContainer ref={loader}>
            {isFetchingNextPage && <Typography>{t('common.loading')}...</Typography>}
          </Styled.LoadMoreContainer>
        </LayoutStyled.ListContainer>
      </Grid>
      <LabelModal
        open={isEditLabelModalOpen}
        labelId={editedLabel?.id}
        initialValues={editedLabel}
        onClose={() => setEditLabelModalOpen(false)}
        onSave={handleSave}
        isLoading={
          isAddLabelLoading || isEditLabelLoading || isDeleteLabelLoading
        }
      />
    </RouteContainer>
  )
}

export { LabelsScreen }
